/* LL: one delegated controller also covers Local's cloned sticky navigation. */
(() => {
  if (window.LLShopPanel) return;
  // LL: the Shop All label remains a real link. Hover opens the panel; the
  // adjacent button, mobile menu button and explicit #shop-all links toggle it.
  const panelTriggerSelector = '#site-header .mobile-menu-button, [data-ll-shop-toggle], a[href$="#shop-all"], #site-menu-sidebar a[href$="/collections/all"]';
  const hoverTriggerSelector = '[data-ll-shop-link], [data-ll-shop-toggle], a[href$="#shop-all"]';
  const hoverAreaSelector = `${panelTriggerSelector}, ${hoverTriggerSelector}`;
  const desktop = window.matchMedia('(min-width: 990px)');
  const hoverDevice = window.matchMedia('(min-width: 990px) and (hover: hover)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let panel = null;

  function syncWhyLinks() {
    const destination = document.getElementById('site-header')?.dataset.llWhyLink || '/pages/how-it-works?view=why';
    document.querySelectorAll('#site-header a[href], .sticky-header a[href], #site-menu-sidebar a[href], [data-ll-shop-panel] a[href]').forEach(link => {
      const title = link.textContent.replace(/[›→↗]/g, '').trim().toLowerCase();
      // Write only when different: ll-header.js watches href changes and fires ll:header:updated,
      // which calls this again, so an unconditional write loops about 60 times a second.
      if ((title === 'why it works' || title === 'why lean living') && link.getAttribute('href') !== destination) link.setAttribute('href', destination);
    });
  }

  class ShopPanel {
    constructor(root) {
      this.root = root;
      this.owner = root.parentElement;
      this.sheet = root.querySelector('.ll-shop-panel__sheet');
      this.tabs = [...root.querySelectorAll('[role="tab"]')];
      this.contents = [...root.querySelectorAll('[role="tabpanel"]')];
      this.opened = false;
      this.inerted = [];
      // Escape header stacking/transform contexts without changing Local files.
      document.body.append(root);
      this.select(0);
      this.syncTriggers();
    }

    syncTriggers() {
      syncWhyLinks();
      document.querySelectorAll(panelTriggerSelector).forEach(link => {
        for (const [name, value] of [['aria-controls', this.sheet.id], ['aria-expanded', String(this.opened)], ['aria-haspopup', 'dialog']]) {
          if (link.getAttribute(name) !== value) link.setAttribute(name, value);
        }
      });
    }

    select(index, focus = false) {
      const selected = Math.max(0, Math.min(Number(index) || 0, this.tabs.length - 1));
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === selected));
        tab.tabIndex = i === selected ? 0 : -1;
        this.contents[i].hidden = i !== selected;
      });
      this.index = selected;
      if (focus) {
        this.tabs[selected]?.focus({ preventScroll: true });
        // LL Brief 99D: all three phone tabs fit; scrolling a fixed sheet here can nudge it sideways at 360px.
        if (desktop.matches) this.tabs[selected]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    position() {
      const header = document.getElementById('site-header');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      const top = `${Math.max(0, Math.min(bottom, window.innerHeight - 100))}px`;
      if (this.root.style.getPropertyValue('--ll-shop-top') !== top) this.root.style.setProperty('--ll-shop-top', top);
      const tablist = this.root.querySelector('[role="tablist"]');
      const orientation = desktop.matches ? 'vertical' : 'horizontal';
      if (tablist.getAttribute('aria-orientation') !== orientation) tablist.setAttribute('aria-orientation', orientation);
    }

    lock() {
      if (this.savedBody) return;
      this.scrollY = window.scrollY;
      this.savedBody = {};
      ['position', 'top', 'width', 'overflow', 'padding-right'].forEach(key => {
        this.savedBody[key] = [document.body.style.getPropertyValue(key), document.body.style.getPropertyPriority(key)];
      });
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollY}px`, width: '100%', overflow: 'hidden', paddingRight: `${padding + scrollbar}px` });
      [...document.body.children].forEach(element => {
        if (element !== this.root && !element.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) {
          element.inert = true;
          this.inerted.push(element);
        }
      });
    }

    unlock() {
      if (!this.savedBody) return;
      Object.entries(this.savedBody).forEach(([key, [value, priority]]) => {
        if (value) document.body.style.setProperty(key, value, priority);
        else document.body.style.removeProperty(key);
      });
      this.savedBody = null;
      this.inerted.forEach(element => { element.inert = false; });
      this.inerted = [];
      window.scrollTo({ top: this.scrollY, behavior: 'instant' });
    }

    mode() {
      this.position();
      if (!this.opened) return;
      if (desktop.matches) {
        this.sheet.removeAttribute('aria-modal');
        this.unlock();
        this.position();
      } else {
        this.sheet.setAttribute('aria-modal', 'true');
        this.lock();
        if (!this.root.contains(document.activeElement)) this.focus();
      }
    }

    focus() { (this.tabs[this.index] || this.sheet).focus({ preventScroll: true }); }

    open(index = 0, trigger = null, hover = false, keyboard = !hover) {
      // LL: header sheets are mutually exclusive, including pending hover timers.
      window.LLOfferPanel?.close();
      const pendingDrawer = this.handoffTimer ? this.drawer : null;
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      clearTimeout(this.hideTimer);
      clearTimeout(this.handoffTimer);
      clearTimeout(this.restoreTimer);
      if (this.opened) {
        if (trigger) this.trigger = trigger;
        if (!hover) { this.select(index); this.focus(); }
        return;
      }
      this.keyboardOpened = keyboard;
      this.trigger = trigger || document.activeElement;
      const drawer = document.getElementById('site-menu-sidebar');
      this.drawer = pendingDrawer || (drawer && (drawer.opened || drawer.classList.contains('sidebar--opened')) ? drawer : null);
      const reveal = () => {
        this.handoffTimer = null;
        this.opened = true;
        this.select(index);
        this.root.hidden = false;
        this.root.inert = false;
        this.position();
        // Flush the closed transform so the first opening also animates.
        void this.sheet.offsetWidth;
        this.root.classList.add('is-open');
        this.mode();
        this.syncTriggers();
        if (!hover) this.focus();
      };
      if (this.drawer) {
        // Local hide() clears its inert/scroll state immediately and display after 501ms.
        // Wait for that cleanup before our modal owns focus and background isolation.
        if (typeof this.drawer.hide === 'function') this.drawer.hide();
        else this.drawer.querySelector('[data-js-close]')?.click();
        this.handoffTimer = setTimeout(reveal, 510);
      } else reveal();
    }

    close(restore = true, immediate = false) {
      // Closing a sheet can expose a trigger under a stationary pointer. Do not
      // reopen it on that synthetic pointerover immediately after Escape.
      if (restore) this.suppressHover = true;
      const pending = Boolean(this.handoffTimer);
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      clearTimeout(this.handoffTimer);
      this.handoffTimer = null;
      if (!this.opened) {
        // Escape during the hand-off still returns to the originating menu link.
        if (pending && restore) this.restoreTimer = setTimeout(() => this.restoreFocus(), 510);
        return;
      }
      this.opened = false;
      this.root.classList.remove('is-open');
      this.root.inert = true;
      this.unlock();
      this.sheet.removeAttribute('aria-modal');
      this.syncTriggers();
      if (immediate) { clearTimeout(this.hideTimer); this.root.hidden = true; }
      else this.hideTimer = setTimeout(() => { this.root.hidden = true; }, reducedMotion.matches ? 0 : 300);
      if (restore) this.restoreFocus();
      else this.drawer = null;
    }

    restoreFocus() {
      if (this.trigger?.isConnected) {
        // A trigger in Local's closed drawer must be visible before focus can return.
        if (this.drawer?.contains(this.trigger) && typeof this.drawer.show === 'function') this.drawer.show();
        this.trigger.focus({ preventScroll: true });
      }
      this.drawer = null;
    }

    destroy() {
      this.close(false);
      clearTimeout(this.hideTimer);
      clearTimeout(this.restoreTimer);
      this.root.remove();
      document.querySelectorAll(panelTriggerSelector).forEach(link => {
        ['aria-controls', 'aria-expanded', 'aria-haspopup'].forEach(name => link.removeAttribute(name));
      });
    }
  }

  function init() {
    const root = document.querySelector('[data-ll-shop-panel]');
    if (panel && (!panel.owner.isConnected || (root && root !== panel.root))) {
      panel.destroy();
      panel = null;
    }
    if (!panel && root?.isConnected) panel = new ShopPanel(root);
    panel?.syncTriggers();
  }
  // Public tab indices are zero-based; opening without an index selects the first tab.
  window.LLShopPanel = { open: index => { init(); panel?.open(index); }, close: (restore = true, immediate = false) => panel?.close(restore, immediate), init };

  document.addEventListener('click', event => {
    const trigger = event.target.closest(panelTriggerSelector);
    const planLink = event.target.closest('[data-ll-plan-target]');
    if (trigger) init();
    if (!panel) return;
    if (trigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (panel.opened) panel.close();
      else panel.open(0, trigger, false, event.detail === 0);
    } else if (event.target.closest('[data-ll-shop-close]') && panel.root.contains(event.target)) {
      panel.close();
    } else if (panel.root.contains(event.target) && event.target.closest('[role="tab"]')) {
      panel.select(event.target.closest('[role="tab"]').dataset.llTab, true);
    } else if (planLink && panel.root.contains(planLink)) {
      const href = planLink.getAttribute('href') || '';
      if (!href.startsWith('#')) {
        panel.close(false);
        return;
      }
      event.preventDefault();
      let target = null;
      try { target = document.querySelector(href); } catch { target = null; }
      panel.close(false);
      if (!target) {
        window.location.assign(planLink.dataset.llPlanFallback || '/pages/how-it-works?view=plan');
        return;
      }
      if (location.hash !== href) history.pushState(null, '', href);
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      const firstName = target.querySelector('input[name="first_name"]');
      if (firstName) requestAnimationFrame(() => firstName.focus({ preventScroll: true }));
    } else if (panel.opened && event.target.closest('a[href]')) {
      panel.close(false);
    }
  }, true);

  document.addEventListener('keyup', event => {
    if (panel && event.target.closest('#site-header .mobile-menu-button') && ['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('keydown', event => {
    const trigger = event.target.closest(panelTriggerSelector);
    if (trigger) init();
    if (!panel) return;
    if (trigger && ['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (panel.opened) panel.close();
      else panel.open(0, trigger);
      return;
    }
    if (!panel.opened && !panel.handoffTimer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      panel.close();
      return;
    }
    const tab = event.target.closest('[role="tab"]');
    if (tab && panel.root.contains(tab)) {
      const previous = desktop.matches ? 'ArrowUp' : 'ArrowLeft';
      const next = desktop.matches ? 'ArrowDown' : 'ArrowRight';
      let index = Number(tab.dataset.llTab);
      if (event.key === previous) index = (index - 1 + panel.tabs.length) % panel.tabs.length;
      else if (event.key === next) index = (index + 1) % panel.tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = panel.tabs.length - 1;
      else index = null;
      if (index !== null) { event.preventDefault(); panel.select(index, true); }
    }
    if (event.key === 'Tab' && !desktop.matches) {
      const items = [...panel.sheet.querySelectorAll('a[href], button, [tabindex="0"]')].filter(el => !el.disabled && el.getClientRects().length && !el.closest('[hidden], [inert]') && el.tabIndex >= 0);
      const first = items[0] || panel.sheet;
      const last = items[items.length - 1] || panel.sheet;
      if (event.shiftKey && (document.activeElement === first || !panel.sheet.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.sheet.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
  }, true);

  const inHoverArea = target => target instanceof Element && (panel.sheet.contains(target) || target.closest(`${hoverAreaSelector}, #site-header, .sticky-header.show`));
  document.addEventListener('pointerover', event => {
    if (!hoverDevice.matches || event.pointerType === 'touch') return;
    const hoverTrigger = event.target.closest(hoverTriggerSelector);
    if (hoverTrigger) init();
    if (!panel) return;
    if (inHoverArea(event.target)) clearTimeout(panel.leaveTimer);
    // LL: pointing at a tab shows its panel (Huel style); a short delay stops flicker while crossing tabs.
    const hoverTab = event.target.closest('[role="tab"]');
    if (hoverTab && panel.opened && panel.root.contains(hoverTab) && !hoverTab.contains(event.relatedTarget)) {
      clearTimeout(panel.tabTimer);
      panel.tabTimer = setTimeout(() => { if (panel?.opened) panel.select(hoverTab.dataset.llTab); }, 90);
    }
    if (hoverTrigger && !panel.suppressHover && !hoverTrigger.contains(event.relatedTarget)) {
      clearTimeout(panel.hoverTimer);
      panel.hoverTimer = setTimeout(() => panel?.open(0, hoverTrigger, true), 120);
    }
  });
  document.addEventListener('pointermove', () => {
    if (panel) panel.suppressHover = false;
  }, { passive: true });
  document.addEventListener('pointerout', event => {
    if (!panel || !hoverDevice.matches || event.pointerType === 'touch') return;
    const hoverTrigger = event.target.closest(hoverTriggerSelector);
    if (hoverTrigger && !hoverTrigger.contains(event.relatedTarget)) clearTimeout(panel.hoverTimer);
    const leftTab = event.target.closest('[role="tab"]');
    if (leftTab && !leftTab.contains(event.relatedTarget)) clearTimeout(panel.tabTimer);
    if (!inHoverArea(event.target)) return;
    if (inHoverArea(event.relatedTarget)) return;
    clearTimeout(panel.leaveTimer);
    panel.leaveTimer = setTimeout(() => {
      if (panel && !panel.keyboardOpened) panel.close(false);
    }, 200);
  });
  document.addEventListener('focusin', event => {
    if (event.target.closest(panelTriggerSelector)) init();
    if (!panel?.opened) return;
    if (!desktop.matches && !panel.root.contains(event.target)) panel.focus();
    else if (desktop.matches && !panel.root.contains(event.target) && !event.target.closest(panelTriggerSelector)) panel.close(false);
  });
  let positionFrame = 0;
  const queuePosition = () => {
    if (!panel?.opened || positionFrame) return;
    positionFrame = requestAnimationFrame(() => { positionFrame = 0; if (panel?.opened) panel.position(); });
  };
  window.addEventListener('resize', queuePosition, { passive: true });
  window.addEventListener('scroll', queuePosition, { passive: true });
  desktop.addEventListener('change', () => panel?.mode());
  window.addEventListener('pagehide', () => panel?.close(false));
  window.addEventListener('popstate', () => panel?.close(false));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    if (panel && (event.detail?.sectionId === panel.root.dataset.sectionId || event.target === panel.owner)) { panel.destroy(); panel = null; }
  });
  document.addEventListener('shopify:block:select', event => {
    init();
    if (!panel) return;
    const id = event.detail?.blockId;
    const block = [...panel.root.querySelectorAll('[data-ll-block-id]')].find(el => el.dataset.llBlockId === id);
    if (block) panel.open(Number(block.dataset.llTab ?? block.closest('[data-ll-tab-content]')?.dataset.llTabContent));
  });
  // Header owns clone/fragment discovery; unrelated DOM mutations are ignored.
  document.addEventListener('ll:header:updated', () => panel?.syncTriggers());
  const idleInit = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(init, {timeout:2000});
    else setTimeout(init, 200);
  };
  if (document.readyState === 'complete') idleInit();
  else window.addEventListener('load', idleInit, {once:true});
})();
